// services/frontend/src/pages/SignupStep3Page.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DaumPostcodeEmbed from 'react-daum-postcode';
import SignupFixedBottomButton from '../components/signup/SignupFixedBottomButton';
import SignupPageLayout from '../components/signup/SignupPageLayout';
import SignupTextInput from '../components/signup/SignupTextInput';
import MessageModal from '../components/MessageModal';
import { useAuth } from '../auth/useAuth';
import { getSignupDraft, patchSignupDraft, clearSignupDraft } from '../utils/signupDraft';
import { getOrCreateDeviceId } from '../utils/auth';
import {
  markSmartHealingLinkedUser,
  markSmartHealingOriginSession,
} from '../utils/smartHealingAccess';

// [회원가입 UI 스타일]
// - Step3 화면의 일부 인라인 스타일(주소 검색 모달 등)을 CSS로 분리하여 유지보수성을 높입니다.
import './SignUp.css';

/**
 * [Why]
 * RN 앱과 동일한 UX의 웹 회원가입 3단계(실명/주소 + KCP 본인인증) 화면입니다.
 * - 이 단계에서 KCP 본인인증을 반드시 수행해야 하며(필수),
 *   인증 성공 후 최종 회원가입 완료 API를 호출합니다.
 *
 * [How]
 * - 현재 단계에서는 화면 뼈대와 draft 저장만 구현합니다.
 * - KCP 시작/결과 폴링/가입완료 API는 백엔드(mcp-gateway) 계약 확정 후 연결합니다.
 */
export default function SignupStep3Page() {
  const auth = useAuth();
  const navigate = useNavigate();
  const draft = useMemo(() => getSignupDraft() || {}, []);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // KCP 폴링 타이머/컨트롤
  const kcpPollTimerRef = useRef(null);
  const kcpPollStartedAtRef = useRef(0);
  // [KCP 팝업 핸들링]
  // - KCP 인증 흐름 중 내부 리다이렉트/서브 팝업이 열리는 경우, 최초로 열어둔 팝업이 남을 수 있습니다.
  // - 부모창(회원가입 Step3)에서 팝업 참조를 보관하고, 인증 완료 시점에 안전하게 닫아줍니다.
  const kcpPopupRef = useRef(null);

  useEffect(() => {
    // [회원가입 UX]
    // - Step1/Step2 선행이 없으면 Step3로 직접 진입할 수 없도록 방지
    const latest = getSignupDraft() || {};
    if (!latest.email || !latest.password) {
      // [테스트/운영 편의]
      // - 이전 진행 상태(특히 KCP 인증 상태)가 남아 Step3에 잔류하는 문제를 방지하기 위해
      //   Step1 필수 데이터가 없으면 draft를 초기화하고 Step1로 되돌립니다.
      clearSignupDraft();
      navigate('/signup/step1', { replace: true });
      return;
    }
    if (!latest.agreeTerms || !latest.agreePrivacy) {
      navigate('/signup/step2', { replace: true });
    }
  }, [navigate]);

  const [userName, setUserName] = useState(draft.userName || '');
  const [postalCode, setPostalCode] = useState(draft.postalCode || '');
  const [address, setAddress] = useState(draft.address || '');
  const [detailAddress, setDetailAddress] = useState(draft.detailAddress || '');

  // KCP 결과값(예: ci/di/성별/생년월일/휴대폰번호 등)은 백엔드 연동 후 채워집니다.
  const [kcpVerified, setKcpVerified] = useState(!!draft.kcpVerified);

  // KCP 결과(가입 완료 요청에 필요한 필드)
  const [kcpSessionId, setKcpSessionId] = useState(draft.kcpSessionId || '');
  const [kcpCi, setKcpCi] = useState(draft.kcpCi || '');
  const [kcpDi, setKcpDi] = useState(draft.kcpDi || '');
  const [kcpPhoneNo, setKcpPhoneNo] = useState(draft.kcpPhoneNo || '');
  const [kcpBirthDay, setKcpBirthDay] = useState(draft.kcpBirthDay || '');
  const [kcpSexCode, setKcpSexCode] = useState(draft.kcpSexCode || '');
  const [kcpStatusText, setKcpStatusText] = useState('');
  const [kcpErrorText, setKcpErrorText] = useState('');

  // [로컬 개발용] KCP Mock 인증번호 입력
  // - mcp-gateway에서 ENABLE_KCP_MOCK=true 인 경우, /signup/kcp/start 응답에 mock=true가 포함됩니다.
  // - 이 경우 팝업을 띄우지 않고 테스트 인증번호 6자리 입력으로 인증을 완료합니다.
  const [kcpMockMode, setKcpMockMode] = useState(false);
  const [kcpMockCode, setKcpMockCode] = useState('');
  const [kcpMockSubmitting, setKcpMockSubmitting] = useState(false);

  const [submitTried, setSubmitTried] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formErrorText, setFormErrorText] = useState('');
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [signupSuccessModalOpen, setSignupSuccessModalOpen] = useState(false);
  const [kcpCountdown, setKcpCountdown] = useState(null); // 남은 시간(초) 표시용
  const kcpCountdownTimerRef = useRef(null);

  const parseAxiosError = (e) => {
    // [주의]
    // - 회원가입 UX에서 네트워크/검증/서버 오류를 사용자에게 과도하게 노출하지 않기 위해
    //   대표 메시지로 축약합니다.
    if (e && e.message === 'Network Error' && !e.response) {
      return '네트워크 연결에 문제가 있습니다. 잠시 후 다시 시도해 주세요.';
    }
    const status = e?.response?.status;
    const data = e?.response?.data;
    const backendDetail = (data && (data.detail || data.message)) || '';
    if (status === 400 || status === 409 || status === 422) {
      return backendDetail || '입력값을 확인해 주세요.';
    }
    if (status && status >= 500) {
      return backendDetail || '서버 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
    }
    return backendDetail || e?.message || '요청 처리 중 오류가 발생했습니다.';
  };

  const stopKcpPolling = () => {
    if (kcpPollTimerRef.current) {
      clearInterval(kcpPollTimerRef.current);
      kcpPollTimerRef.current = null;
    }
    if (kcpCountdownTimerRef.current) {
      clearInterval(kcpCountdownTimerRef.current);
      kcpCountdownTimerRef.current = null;
    }
    setKcpCountdown(null);
  };

  const resetKcpState = () => {
    // [회원가입 UX]
    // - 이미 인증이 완료된 상태에서 '다시 인증'을 수행할 수 있도록 기존 KCP 상태를 초기화합니다.
    // - 폴링 타이머/카운트다운/세션/CI/DI 등 모든 인증 결과를 함께 정리하여 상태 꼬임을 방지합니다.
    stopKcpPolling();
    setKcpVerified(false);
    setKcpSessionId('');
    setKcpCi('');
    setKcpDi('');
    setKcpPhoneNo('');
    setKcpBirthDay('');
    setKcpSexCode('');
    setKcpStatusText('');
    setKcpErrorText('');
    setKcpMockMode(false);
    setKcpMockCode('');
    setKcpMockSubmitting(false);
    patchSignupDraft({
      kcpVerified: false,
      kcpSessionId: '',
      kcpCi: '',
      kcpDi: '',
      kcpPhoneNo: '',
      kcpBirthDay: '',
      kcpSexCode: '',
    });
  };

  useEffect(() => {
    return () => {
      // [중요] 화면 이탈 시 폴링 타이머 정리
      stopKcpPolling();
    };
  }, []);

  useEffect(() => {
    // [웹 UX]
    // - 백엔드 콜백 팝업에서 postMessage(type=KCP_V2_CALLBACK/KCP_CALLBACK)를 보내는 경우,
    //   부모창에서도 팝업을 닫아 초기 팝업 잔류 문제를 방지합니다.
    const onMessage = (event) => {
      try {
        const data = event?.data;
        if (!data || typeof data !== 'object') return;
        const messageType = String(data.type || '');
        if (messageType !== 'KCP_V2_CALLBACK' && messageType !== 'KCP_CALLBACK') return;

        const popup = kcpPopupRef.current;
        if (popup && !popup.closed) {
          try {
            popup.close();
          } catch (e) {
            // 팝업 닫기 실패는 치명적이지 않으므로 무시합니다.
          }
        }
      } catch (e) {
        // 메시지 파싱 오류는 무시합니다.
      }
    };

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, []);

  const canSubmit =
    !!userName &&
    !!address &&
    !!postalCode &&
    !!detailAddress &&
    !!kcpVerified &&
    !!kcpCi &&
    !!kcpDi &&
    !!kcpPhoneNo &&
    !!kcpBirthDay &&
    !!kcpSexCode &&
    !loading;

  const handleSaveDraft = () => {
    patchSignupDraft({
      userName: String(userName || '').trim(),
      postalCode: String(postalCode || '').trim(),
      address: String(address || '').trim(),
      detailAddress: String(detailAddress || '').trim(),
      kcpVerified: !!kcpVerified,
      kcpSessionId: String(kcpSessionId || '').trim(),
      kcpCi: String(kcpCi || '').trim(),
      kcpDi: String(kcpDi || '').trim(),
      kcpPhoneNo: String(kcpPhoneNo || '').trim(),
      kcpBirthDay: String(kcpBirthDay || '').trim(),
      kcpSexCode: String(kcpSexCode || '').trim(),
    });
  };

  const openEmptyKcpPopup = () => {
    // [중요]
    // - 크롬/사파리 등 브라우저는 사용자 클릭 이벤트와 분리된 비동기 컨텍스트(axios await 이후)에서
    //   window.open을 호출하면 팝업을 차단하는 경우가 많습니다.
    // - 따라서 "클릭 순간"에 빈 팝업을 먼저 열고, 응답을 받은 뒤 해당 팝업에 폼을 주입해 submit 합니다.
    // [중요]
    // - '다시 인증' 등 재시도 시 동일한 popupName을 쓰면, 브라우저가 기존 창을 재사용합니다.
    // - 이때 기존 창이 이미 KCP 도메인(cert.kcp.co.kr)으로 이동해 있으면 cross-origin 정책 때문에
    //   popup.document 접근이 즉시 차단되어 오류가 발생합니다.
    // - 따라서 (1) 기존 팝업이 남아있으면 먼저 닫고 (2) 매번 고유한 이름으로 새 팝업을 엽니다.
    try {
      const prev = kcpPopupRef.current;
      if (prev && !prev.closed) prev.close();
    } catch (e) {
      // 팝업 닫기 실패는 무시합니다.
    }

    const popupName = `kcp_identity_verification_${Date.now()}`;
    const popup = window.open('about:blank', popupName, 'width=420,height=720');
    if (!popup) {
      throw new Error('팝업이 차단되어 본인인증 창을 열 수 없습니다. 브라우저 팝업 차단을 해제해 주세요.');
    }
    // [중요] 팝업 참조를 보관하여 인증 완료 시 부모창에서 닫을 수 있게 합니다.
    kcpPopupRef.current = popup;
    try {
      popup.focus();
    } catch (e) {
      // focus 실패는 치명적이지 않으므로 무시합니다.
    }
    return popup;
  };

  const openKcpPopupWithForm = ({ popup, callUrl, formData, method }) => {
    // [Why]
    // - KCP는 일반적인 JSON API 호출이 아니라, 폼 POST로 인증창을 띄우는 방식입니다.
    // - 팝업창을 열고 form을 자동 submit하여 RN 앱과 유사한 UX를 제공합니다.
    if (!popup) {
      throw new Error('본인인증 팝업을 열 수 없습니다. 팝업 차단 여부를 확인해 주세요.');
    }

    const doc = popup.document;
    doc.open();
    doc.write('<html><head><title>KCP 본인인증</title></head><body></body></html>');
    doc.close();

    const form = doc.createElement('form');
    form.setAttribute('method', (method || 'POST').toUpperCase());
    form.setAttribute('action', callUrl);
    form.setAttribute('accept-charset', 'euc-kr');

    const entries = formData && typeof formData === 'object' ? Object.entries(formData) : [];
    entries.forEach(([k, v]) => {
      const input = doc.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', String(k));
      input.setAttribute('value', String(v ?? ''));
      form.appendChild(input);
    });

    doc.body.appendChild(form);
    form.submit();
  };

  const startKcpPolling = async (sessionId) => {
    // [정책]
    // - 폴링은 무한정 지속되면 안 되므로 최대 2분 제한을 둡니다.
    const POLL_INTERVAL_MS = 1500;
    const POLL_TIMEOUT_MS = 120000;

    stopKcpPolling();
    kcpPollStartedAtRef.current = Date.now();
    setKcpCountdown(Math.ceil(POLL_TIMEOUT_MS / 1000));

    // [KCP 타임아웃 표시용 타이머]
    const countdownTimer = setInterval(() => {
      const elapsed = Date.now() - (kcpPollStartedAtRef.current || 0);
      const remain = Math.max(0, Math.ceil((POLL_TIMEOUT_MS - elapsed) / 1000));
      setKcpCountdown(remain);
      if (remain <= 0) {
        clearInterval(countdownTimer);
      }
    }, 1000);
    kcpCountdownTimerRef.current = countdownTimer;

    const pollOnce = async () => {
      const elapsed = Date.now() - (kcpPollStartedAtRef.current || 0);
      if (elapsed > POLL_TIMEOUT_MS) {
        stopKcpPolling();
        setKcpStatusText('');
        setKcpErrorText('본인인증 결과 확인 시간이 초과되었습니다. 다시 시도해 주세요.');
        return;
      }

      try {
        const { data } = await axios.get(`${apiUrl}/api/v1/signup/kcp/result/${sessionId}`);
        const status = String(data?.status || '').toUpperCase();

        if (status === 'PENDING') {
          setKcpStatusText('본인인증 진행 중입니다...');
          return;
        }

        if (status === 'SUCCESS' && data?.success) {
          stopKcpPolling();
          setKcpStatusText('본인인증이 완료되었습니다.');
          setKcpErrorText('');

          // [웹 UX]
          // - 인증 완료 시 최초 팝업이 남아있는 경우가 있으므로 부모창에서 팝업을 닫습니다.
          try {
            const popup = kcpPopupRef.current;
            if (popup && !popup.closed) popup.close();
          } catch (e) {
            // 팝업 닫기 실패는 무시합니다.
          }

          // 서버에서 내려준 결과를 가입 완료 요청에 필요한 형태로 저장
          const nextCi = String(data?.ci || '').trim();
          const nextDi = String(data?.di || '').trim();
          const nextPhone = String(data?.phoneNo || '').trim();
          const nextBirth = String(data?.birthDay || '').trim();
          const nextSex = String(data?.sexCode || '').trim();
          const nextName = String(data?.userName || '').trim();

          if (nextName) {
            setUserName(nextName);
          }
          setKcpCi(nextCi);
          setKcpDi(nextDi);
          setKcpPhoneNo(nextPhone);
          setKcpBirthDay(nextBirth);
          setKcpSexCode(nextSex);
          setKcpVerified(true);

          patchSignupDraft({
            kcpVerified: true,
            kcpSessionId: String(sessionId),
            kcpCi: nextCi,
            kcpDi: nextDi,
            kcpPhoneNo: nextPhone,
            kcpBirthDay: nextBirth,
            kcpSexCode: nextSex,
            // KCP에서 내려온 이름을 우선 적용(불일치 방지)
            userName: nextName || String(userName || '').trim(),
          });
          return;
        }

        // FAILED / NOT_FOUND 등
        stopKcpPolling();
        setKcpStatusText('');
        setKcpErrorText(String(data?.message || '본인인증에 실패했습니다.'));

        // [웹 UX]
        // - 실패/만료/세션없음 등 종료 상태에서도 팝업이 남지 않도록 닫기를 시도합니다.
        try {
          const popup = kcpPopupRef.current;
          if (popup && !popup.closed) popup.close();
        } catch (e) {
          // 팝업 닫기 실패는 무시합니다.
        }
      } catch (e) {
        // 폴링 중 네트워크 오류는 즉시 실패로 종료하지 않고 메시지만 업데이트
        setKcpErrorText(parseAxiosError(e));
      }
    };

    // 즉시 1회 수행 후 interval 시작
    await pollOnce();
    const pollInterval = setInterval(pollOnce, POLL_INTERVAL_MS);
    // 폴링 interval을 별도 ref에 보관하여 stopKcpPolling에서 정리
    kcpPollTimerRef.current = pollInterval;
  };

  const handleStartKcp = async () => {
    // [KCP 본인인증 흐름]
    // 1) /api/v1/signup/kcp/start 호출 (input_name)
    // 2) 응답(callUrl/formData/sessionId)로 팝업 폼 자동 submit
    // 3) /api/v1/signup/kcp/result/{sessionId} 폴링으로 결과 확인

    // [회원가입 UX]
    // - 이미 인증 완료 상태에서 다시 누른 경우, 기존 인증 상태를 정리하고 재인증을 시작합니다.
    if (kcpVerified) {
      resetKcpState();
      setKcpStatusText('본인인증을 다시 진행합니다.');
    }

    setLoading(true);
    try {
      setKcpErrorText('');
      setKcpStatusText('');

      const nameTrimmed = String(userName || '').trim();
      if (!nameTrimmed) {
        setKcpErrorText('이름을 입력해 주세요.');
        return;
      }

      // [중요] 팝업은 반드시 사용자 클릭 이벤트 컨텍스트에서 먼저 열어야 합니다.
      // - axios await 이후에 window.open을 호출하면 브라우저가 팝업을 차단할 수 있습니다.
      const popup = openEmptyKcpPopup();

      handleSaveDraft();

      const { data } = await axios.post(`${apiUrl}/api/v1/signup/kcp/start`, {
        input_name: nameTrimmed,
      });

      if (!data?.success) {
        try {
          popup.close();
        } catch (e) {
          // 팝업 닫기 실패는 무시합니다.
        }
        setKcpErrorText(String(data?.message || '본인인증 시작에 실패했습니다.'));
        return;
      }

      const sessionId = String(data?.sessionId || '').trim();
      const isMock = !!data?.mock;

      // [운영 안전장치]
      // - Mock UI(테스트 인증번호 입력)는 로컬 개발에서만 허용합니다.
      // - 스테이징/운영(production 빌드)에서 서버가 실수로 mock=true를 내려줘도
      //   사용자에게 테스트 문구가 노출되지 않도록 여기에서 차단합니다.
      // - 로컬은 Docker 빌드 특성상 NODE_ENV=production으로 실행되는 경우가 있어,
      //   REACT_APP_APP_ENV(dev/local)를 1순위로 사용하고, 미주입 시 hostname(localhost)로 폴백합니다.
      const appEnv = String(process.env.REACT_APP_APP_ENV || '').toLowerCase();
      const allowMockByEnv = appEnv === 'dev' || appEnv === 'local';
      const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
      const allowMockUi = process.env.NODE_ENV !== 'production' || allowMockByEnv || isLocalHost;

      // [로컬 개발용] Mock 모드
      // - 팝업은 즉시 닫고, 테스트 인증번호 입력 UI를 노출합니다.
      if (isMock && allowMockUi) {
        try {
          popup.close();
        } catch (e) {
          // 팝업 닫기 실패는 무시합니다.
        }

        if (!sessionId) {
          setKcpErrorText('본인인증 시작 응답이 올바르지 않습니다.');
          return;
        }

        setKcpMockMode(true);
        setKcpMockCode('');
        setKcpSessionId(sessionId);
        patchSignupDraft({ kcpSessionId: sessionId });
        setKcpStatusText('테스트 인증번호를 입력해 주세요.');
        setKcpErrorText('');
        stopKcpPolling();
        return;
      }

      if (isMock && !allowMockUi) {
        // [운영 UX]
        // - production 빌드에서는 테스트 문구를 노출하지 않고 일반 오류로 처리합니다.
        try {
          popup.close();
        } catch (e) {
          // 팝업 닫기 실패는 무시합니다.
        }
        setKcpErrorText('본인인증을 시작할 수 없습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }

      const callUrl = String(data?.callUrl || '').trim();
      const formData = data?.formData || {};
      const method = String(data?.method || 'POST');

      if (!sessionId || !callUrl) {
        try {
          popup.close();
        } catch (e) {
          // 팝업 닫기 실패는 무시합니다.
        }
        setKcpErrorText('본인인증 시작 응답이 올바르지 않습니다.');
        return;
      }

      setKcpSessionId(sessionId);
      patchSignupDraft({ kcpSessionId: sessionId });

      // Device ID는 회원가입 완료에서도 사용되므로 미리 생성/저장해둔다.
      getOrCreateDeviceId();

      openKcpPopupWithForm({ popup, callUrl, formData, method });
      await startKcpPolling(sessionId);
    } catch (e) {
      // [Why]
      // - 팝업 차단/네트워크 오류 등은 사용자가 즉시 조치할 수 있으므로 화면 메시지로 안내합니다.
      const msg = e?.message ? String(e.message) : '';
      if (msg) {
        setKcpErrorText(msg);
      } else {
        setKcpErrorText(parseAxiosError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyKcpMockCode = async () => {
    // [로컬 개발용] 테스트 인증번호 검증
    // - 검증 성공 시 result 폴링을 통해 SUCCESS 결과를 받아 동일한 후처리 로직을 재사용합니다.
    if (!kcpMockMode) return;

    const sessionId = String(kcpSessionId || '').trim();
    const code = String(kcpMockCode || '').trim();
    if (!sessionId) {
      setKcpErrorText('본인인증 세션을 찾을 수 없습니다. 다시 시도해 주세요.');
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setKcpErrorText('테스트 인증번호 6자리를 입력해 주세요.');
      return;
    }

    setKcpMockSubmitting(true);
    try {
      setKcpErrorText('');
      await axios.post(`${apiUrl}/api/v1/signup/kcp/mock/verify`, {
        session_id: sessionId,
        code,
      });

      // 검증 성공 후 동일한 폴링 로직으로 결과 반영
      await startKcpPolling(sessionId);
      setKcpMockMode(false);
      setKcpMockCode('');
    } catch (e) {
      setKcpErrorText(parseAxiosError(e));
    } finally {
      setKcpMockSubmitting(false);
    }
  };

  const handleAddressComplete = (data) => {
    // [주소 검색 완료 핸들러]
    // - react-daum-postcode onComplete 결과를 기반으로 우편번호/기본주소를 자동 입력합니다.
    const zonecode = String(data?.zonecode || '').trim();
    const addr = String(data?.address || '').trim();
    setPostalCode(zonecode);
    setAddress(addr);
    // 상세주소는 사용자가 직접 입력하도록 초기화/포커스 유도
    setDetailAddress('');
    patchSignupDraft({
      postalCode: zonecode,
      address: addr,
      detailAddress: '',
    });
    setAddressModalOpen(false);
  };

  const handleCompleteSignup = async () => {
    setSubmitTried(true);
    if (!canSubmit) {
      // [회원가입 UX]
      // - 버튼이 비활성화되어 있어도(또는 사용자가 빠르게 클릭한 경우),
      //   어떤 항목이 누락되었는지 친절하게 안내합니다.
      const reasons = [];
      if (!String(userName || '').trim()) reasons.push('이름을 입력해 주세요.');
      if (!String(postalCode || '').trim() || !String(address || '').trim()) {
        reasons.push('주소검색을 통해 우편번호/주소를 입력해 주세요.');
      }
      if (!String(detailAddress || '').trim()) reasons.push('상세주소를 입력해 주세요.');
      if (!kcpVerified) reasons.push('본인인증(KCP)을 완료해 주세요.');
      if (reasons.length > 0) {
        setFormErrorText(reasons[0]);
      } else {
        setFormErrorText('입력값을 확인해 주세요.');
      }
      return;
    }

    setLoading(true);
    try {
      setFormErrorText('');
      handleSaveDraft();

      const latest = getSignupDraft() || {};
      const termsAgreed = !!latest.agreeTerms && !!latest.agreePrivacy;

      if (!termsAgreed) {
        // Step2 재진입 유도(필수 약관 누락 방지)
        navigate('/signup/step2', { replace: true });
        return;
      }

      const deviceId = getOrCreateDeviceId();

      const payload = {
        email: String(latest.email || '').trim(),
        password: String(latest.password || ''),
        terms_agreed: true,
        marketing_agreed: !!latest.agreeMarketing,
        user_name: String(userName || '').trim(),
        postal_code: String(postalCode || '').trim(),
        address1: String(address || '').trim(),
        address2: String(detailAddress || '').trim(),
        phone_no: String(kcpPhoneNo || '').trim(),
        birth_day: String(kcpBirthDay || '').trim(),
        sex_code: String(kcpSexCode || '').trim(),
        ci: String(kcpCi || '').trim(),
        di: String(kcpDi || '').trim(),
        smart_healing_member_id: String(latest.smartHealingMemberId || '').trim() || undefined,
      };

      const { data } = await axios.post(`${apiUrl}/api/v1/signup/complete`, payload, {
        headers: {
          'X-Device-Id': deviceId,
        },
      });

      if (data?.access_token) {
        if (String(latest.smartHealingMemberId || '').trim()) {
          markSmartHealingOriginSession();
          if (data?.user_no) {
            markSmartHealingLinkedUser(data.user_no);
          }
        }

        // [주의]
        // - 회원가입 성공은 store에 즉시 반영하되, 성공 모달은 먼저 보여줍니다.
        // - 이후 모달 확인 시점에 intended route 또는 메인으로 이동합니다.
        auth.actions.loginSuccess(data, {
          source: 'web',
          preserveExistingIntent: true,
        });

        stopKcpPolling();

        // [회원가입 UX]
        // - 가입 완료 후 곧바로 화면을 이동시키면 사용자가 성공 여부를 인지하기 어렵습니다.
        // - 따라서 성공 모달을 먼저 띄우고, 확인 클릭 시 메인으로 이동합니다.
        setSignupSuccessModalOpen(true);
      } else {
        setFormErrorText('자동 로그인 처리가 지연되고 있습니다. 잠시 후 다시 시도해 주세요.');
      }
    } catch (e) {
      setFormErrorText(parseAxiosError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SignupPageLayout
      step={3}
      title="회원가입"
      onBack={() => navigate(-1)}
      bottomFixed={(
        <SignupFixedBottomButton
          label="가입 완료"
          onClick={handleCompleteSignup}
          disabled={!canSubmit}
          loading={loading}
        />
      )}
    >
      <div style={{ display: 'grid', gap: 14 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>실명 인증</div>

        <SignupTextInput
          placeholder="이름(실명)"
          value={userName}
          onChange={(e) => {
            setUserName(e.target.value);
            setFormErrorText('');
          }}
          errorText={submitTried && !userName ? '이름을 입력해 주세요.' : ''}
        />

        <button
          type="button"
          onClick={handleStartKcp}
          disabled={loading || kcpVerified}
          style={{
            // [모바일 UX 정렬]
            // - 인증 완료 시 버튼 문구/색상으로만 상태를 표현하고, 재인증은 막습니다.
            width: '100%',
            height: 46,
            borderRadius: 12,
            border: kcpVerified ? '1px solid #2f6ad8' : '1px solid #c9d8ff',
            background: kcpVerified ? '#f5f8ff' : '#fff',
            color: '#1f3d7a',
            cursor: kcpVerified ? 'default' : 'pointer',
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          {loading ? '처리 중...' : (kcpVerified ? '이름(실명인증) 완료' : '이름(실명인증) 확인하기')}
        </button>

        {!!kcpStatusText && (
          <div style={{ color: '#1f3d7a', fontSize: 13 }}>
            {kcpStatusText}
            {kcpCountdown !== null ? ` (${kcpCountdown}s)` : ''}
          </div>
        )}

        {!!kcpErrorText && (
          <div style={{ color: '#b91c1c', fontSize: 13 }}>{kcpErrorText}</div>
        )}

        {kcpMockMode && !kcpVerified && (
          <div style={{ marginTop: 6, display: 'grid', gap: 8 }}>
            <div style={{ fontSize: 13, color: '#6b7280' }}>
              로컬 개발 환경에서는 테스트 인증번호로 본인인증을 진행합니다.
            </div>
            <SignupTextInput
              placeholder="테스트 인증번호 6자리"
              value={kcpMockCode}
              onChange={(e) => setKcpMockCode(e.target.value)}
              errorText=""
            />
            <button
              type="button"
              onClick={handleVerifyKcpMockCode}
              disabled={kcpMockSubmitting || loading}
              style={{ width: '100%', height: 44, borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 15 }}
            >
              {kcpMockSubmitting ? '확인 중...' : '인증번호 확인'}
            </button>
          </div>
        )}

        <div style={{ fontWeight: 600, margin: '10px 0 4px' }}>주소 입력</div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <SignupTextInput
              placeholder="우편번호"
              value={postalCode}
              // [회원가입 UX]
              // - 우편번호/주소는 주소검색(react-daum-postcode) 결과로 자동 입력되며,
              //   사용자가 직접 입력하면 데이터 정합성이 깨질 수 있으므로 입력을 막습니다.
              readOnly
              onChange={() => { }}
              errorText={submitTried && !postalCode ? '주소검색을 통해 우편번호를 입력해 주세요.' : ''}
            />
          </div>
          <button
            type="button"
            onClick={() => {
              // [주소 검색 팝업 오픈]
              handleSaveDraft();
              setAddressModalOpen(true);
            }}
            style={{
              height: 44,
              padding: '0 12px',
              borderRadius: 10,
              border: '1px solid #c9d8ff',
              background: '#f7f9ff',
              color: '#1f3d7a',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            주소검색
          </button>
        </div>

        <SignupTextInput
          placeholder="기본주소"
          value={address}
          // [회원가입 UX]
          // - 주소는 주소검색(react-daum-postcode) 결과로 자동 입력되며,
          //   사용자가 직접 입력할 수 없도록 처리합니다.
          readOnly
          onChange={() => { }}
          errorText={submitTried && !address ? '주소검색을 통해 주소를 입력해 주세요.' : ''}
        />

        <SignupTextInput
          placeholder="상세주소"
          value={detailAddress}
          onChange={(e) => {
            setDetailAddress(e.target.value);
            setFormErrorText('');
          }}
          errorText={submitTried && !detailAddress ? '상세주소를 입력해 주세요.' : ''}
        />

        {!!formErrorText && (
          <div style={{ color: '#b91c1c', fontSize: 13 }}>{formErrorText}</div>
        )}

        {/* [모바일 UX] 불필요한 경고 제거: 필수 안내는 에러 발생 시에만 노출 */}
      </div>

      {addressModalOpen && (
        <div className="signup-modal-overlay">
          <div className="signup-modal">
            <div className="signup-modal-header">
              <div className="signup-modal-title">주소 검색</div>
              <button
                type="button"
                onClick={() => setAddressModalOpen(false)}
                className="signup-modal-close"
              >
                ✕
              </button>
            </div>
            <div className="signup-modal-body">
              <DaumPostcodeEmbed
                onComplete={handleAddressComplete}
                style={{ width: '100%', height: '100%' }}
                scriptUrl="https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js"
              />
            </div>
          </div>
        </div>
      )}

      <MessageModal
        isOpen={signupSuccessModalOpen}
        onClose={() => {
          // [회원가입 UX]
          // - 모달 확인 후 draft를 정리하고, 보호 라우트에서 기억한 intended route가 있으면 우선 복귀합니다.
          setSignupSuccessModalOpen(false);
          clearSignupDraft();
          const intent = auth.actions.consumeIntent();
          navigate(intent?.path || '/', { replace: true });
        }}
        title="회원가입"
        message="회원가입이 완료되었습니다."
        type="info"
      />
    </SignupPageLayout>
  );
}
